import { Globe, Users } from 'lucide-react';
import { roleKind } from '@/components/custom/share-role-kind';

/**
 * Typed by the fields read, with the audience a plain string: reports and
 * dashboards each declare their own `ShareAudience`, and a web bundle already
 * deployed can be sent an audience newer than itself. `shareReach` falls back
 * rather than breaking on one.
 *
 * `subjectName` is optional because a list may not carry it: an API older than
 * this bundle sends none, and `shareNames` falls back to the counts without it.
 */
type Share = { readonly audience: string; readonly subjectName?: string | null };

/** The most names one tooltip spells out; the rest are "and N more". */
const MAX_NAMES = 8;

/**
 * How far a saved report or dashboard reaches, for the card on its list page.
 *
 * Lives here rather than in either route because both lists draw it, and the
 * two copies had already drifted: reports showed a count, dashboards a bare
 * icon.
 *
 * The number counts GRANTS, not people. A role is one grant however many
 * people hold it — expanding membership would mean a directory lookup per
 * card, and a number that moves when someone joins the team. The tooltip
 * names the grants — or, for a list that does not send names, says how many
 * are people and how many roles — so "3" is never mistaken for three people.
 *
 * Only the owner is told who a thing reaches. The server sends a recipient
 * `shares: []` as well, but the gate is here so no caller has to remember it.
 */
export function ShareBadge({ owned, shares }: { owned: boolean; shares: readonly Share[] }) {
  if (!owned || shares.length === 0) return null;
  const label = `Shared with ${shareNames(shares)}`;
  return (
    <span
      className="ml-auto flex shrink-0 items-center gap-1 font-normal text-[10px] text-muted-foreground uppercase tracking-wide"
      title={label}
    >
      <span className="sr-only">{label}</span>
      {tally(shares).everyone ? (
        <>
          <Globe className="size-3" aria-hidden />
          <span aria-hidden>Everyone</span>
        </>
      ) : (
        <>
          <Users className="size-3" aria-hidden />
          <span aria-hidden>{shares.length}</span>
        </>
      )}
    </span>
  );
}

/**
 * `"2 people and 1 role"`, or `"everyone"` — which swallows the rest: once
 * the whole company can see it, naming a role alongside adds nothing. For
 * "Shared with …" on the card and "shared with …" before a delete.
 *
 * An audience this build does not know makes the breakdown incomplete, so it
 * falls back to the bare grant count rather than a sentence that disagrees
 * with the number beside it.
 */
export function shareReach(shares: readonly Share[]): string {
  const { everyone, people, roles } = tally(shares);
  if (everyone) return 'everyone';
  if (people + roles !== shares.length) {
    return `${shares.length} ${shares.length === 1 ? 'recipient' : 'recipients'}`;
  }
  return [
    people > 0 && `${people} ${people === 1 ? 'person' : 'people'}`,
    roles > 0 && `${roles} ${roles === 1 ? 'role' : 'roles'}`,
  ]
    .filter(Boolean)
    .join(' and ');
}

/**
 * `"Jane Doe, Raj Patel and Controller (service role)"` — WHO, for the card's
 * tooltip, where `shareReach` only says how many.
 *
 * People first, then roles, each by name, so the same list always reads the
 * same way. A share that cannot be named still counts: it is the "and 1 more"
 * at the end, so the sentence never names fewer grants than the number beside
 * it. That is a share whose person or role is gone, one from a list that was
 * not sent names at all — and one whose AUDIENCE this build does not know,
 * even if it came with a name: only `user` is read as a person, so a newer
 * kind of grant is never passed off as somebody called "Finance". With no
 * names to give, this is `shareReach`.
 */
export function shareNames(shares: readonly Share[]): string {
  if (tally(shares).everyone) return 'everyone';
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  const people = shares.flatMap((s) =>
    s.audience === 'user' && s.subjectName ? [s.subjectName] : [],
  );
  const roles = shares.flatMap((s) => {
    const kind = roleKind(s.audience);
    return kind && s.subjectName ? [`${s.subjectName} (${kind})`] : [];
  });
  const names = [...people.sort(byName), ...roles.sort(byName)];
  if (names.length === 0) return shareReach(shares);
  const shown = names.slice(0, MAX_NAMES);
  const more = shares.length - shown.length;
  const parts = more > 0 ? [...shown, `${more} more`] : shown;
  return parts.length === 1
    ? (parts[0] as string)
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function tally(shares: readonly Share[]) {
  let everyone = false;
  let people = 0;
  let roles = 0;
  for (const s of shares) {
    if (s.audience === 'everyone') everyone = true;
    else if (s.audience === 'user') people++;
    // Either kind of role. The card says how FAR a thing reaches, and at that
    // distance a system role and a service role are the same thing: one grant
    // covering whoever holds it.
    else if (s.audience === 'role' || s.audience === 'service_role') roles++;
  }
  return { everyone, people, roles };
}
